import * as React from "react";
import InvoiceForm, {InvoiceFormResult} from "./InvoiceForm";
import InvoicingSummaryTable from "./InvoicingSummaryTable";
import {ClientSummary, CreateInvoiceRequest, ErrorResponse, Invoice, SaveInvoiceRequest} from "../api/dtos/dtos";
import locales from "../locales";
import {groupNumber, withCurrency} from "../utils/number-utils";
import {ErrorAlert} from "../common/ErrorAlert";
import {getTotalFromOverridesOrSummaries, InvoiceEntry} from "../api/dtos/summaries";
import {InvoiceSource} from "./invoice";
import {Modal} from "antd";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../app/ApplicationContext";
import furoApi from "../api/FuroApi";
import {showSaveSuccessNotification} from "../utils/dialogs";

interface Props {
  invoice?: Invoice;
  client?: ClientSummary;
  source: InvoiceSource;
  sourceId?: number;
  year?: number;
  month?: number;
  entries?: InvoiceEntry[];
  onSubmit: (id: number) => void;
  onCancel: () => void;
  editable: boolean;
  invoiceComment?: string;
}

interface State {
  entries: InvoiceEntry[];
  error?: ErrorResponse;
}

class InvoiceEditor extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    entries: this.props.entries ?? [],
    error: undefined,
  };

  public render(): React.ReactElement {
    const {client, invoice, editable} = this.props;
    const {entries, error} = this.state;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <InvoiceForm
          invoice={invoice}
          client={client}
          editable={editable}
          invoiceComment={this.props.invoiceComment}
          onFormSubmit={this.onFormSubmit}
          onFormCancel={this.onFormCancel}
        >
          <>
            <h4 style={{textAlign: 'center'}}>{locales.entries}</h4>
            <InvoicingSummaryTable entries={entries} onChange={this.onTableChange} editable={editable}/>
            {this.renderTotal()}
          </>
          {error && <ErrorAlert error={error} style={{marginBottom: 16}}/>}
        </InvoiceForm>
      </>
    );
  }

  private renderTotal = () => {
    const {entries} = this.state;
    const count = entries.length;
    const total = getTotalFromOverridesOrSummaries(entries);
    return (
      <div style={{textAlign: 'right', margin: '8px 0'}}>
        {locales.total} <b>{count}</b> {locales.entry}<br/>
        <b>{entries && withCurrency(groupNumber(total))}</b>
      </div>
    );
  };

  private onFormCancel = () => {
    this.props.onCancel();
  };

  private onFormSubmit = (result: InvoiceFormResult) => {
    Modal.confirm({
      title: this.props.invoice ? locales.save_confirm : locales.invoice_confirm,
      onOk: () => this.props.invoice ? this.saveInvoice(result) : this.createInvoice(result),
      okText: locales.yes,
      cancelText: locales.cancel,
    });
  };

  private saveInvoice = (result: InvoiceFormResult) => {
    const request: SaveInvoiceRequest = {
      entries: this.state.entries,
      paymentType: result.paymentType,
      fulfillment: result.fulfillment,
      deadline: result.deadline,
      comment: result.comment || '',
      continuousFulfillment: result.continuousFulfillment,
    };
    this.setState({error: undefined});
    const promise = furoApi.invoicing.saveInvoice(this.props.invoice!.id, request)
      .then(this.onSaveSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
    return promise;
  };

  private createInvoice = (result: InvoiceFormResult) => {
    const {year, month, client, sourceId, source} = this.props;

    const request: CreateInvoiceRequest = {
      clientId: result.clientId ?? client?.id,
      source,
      sourceId,
      year,
      month,
      entries: this.state.entries,
      paymentType: result.paymentType,
      created: result.created,
      fulfillment: result.fulfillment,
      deadline: result.deadline,
      comment: result.comment,
      continuousFulfillment: result.continuousFulfillment
    };

    const promise = furoApi.invoicing.createInvoice(request)
      .then(this.onSaveSuccess)
      .catch(this.onApiError);

    withLoadingOverlay(promise, this.loadingHandler);
    return promise;
  };

  private onSaveSuccess = (id: number) => {
    showSaveSuccessNotification();
    this.props.onSubmit(id);
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error,});
  };


  private onTableChange = (entries: InvoiceEntry[]) => {
    this.setState({entries});
  }
}

export default InvoiceEditor;



// WEBPACK FOOTER //
// ./src/invoicing/InvoiceEditor.tsx